import { Button, Color, Flex, Icon, IconButton, ProgressBar, Spacing, Text, Tooltip } from '@billy/design-system'
import { MouseEvent, ReactElement, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFecImportSessionContext } from '../../contexts/fecImportSessionContext'
import { appendUniqueIds } from '../../utils/appendUniqueIds'
import { formatFecImportPeriod } from '../../utils/formatFecImportPeriod'
import { isFecImportAnalysisComplete } from '../../utils/isFecImportAnalysisComplete'
import * as Styled from './styles'

interface FecImportAnalysingStepProps {
  onClose: () => void
}

const ANALYSING_PROGRESS_PERCENT = 40

export const FecImportAnalysingStep = ({ onClose }: FecImportAnalysingStepProps): ReactElement => {
  const { i18n, t } = useTranslation()
  const {
    analysingFecImports: fecImports,
    cancellingFecImportIds,
    onCancelAnalysingFile: onCancelFile,
  } = useFecImportSessionContext()
  const [cancelFailedImportIds, setCancelFailedImportIds] = useState<string[]>([])
  const hasPointerDownOnCloseRef = useRef(false)

  const handleClosePointerDown = useCallback(() => {
    hasPointerDownOnCloseRef.current = true
  }, [])

  const handleCloseClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (event.detail !== 0 && !hasPointerDownOnCloseRef.current) {
        return
      }

      hasPointerDownOnCloseRef.current = false
      onClose()
    },
    [onClose],
  )

  const handleCancelFileClick = useCallback(
    (fecImportId: string) => {
      setCancelFailedImportIds((currentIds) => currentIds.filter((importId) => importId !== fecImportId))
      onCancelFile(fecImportId).catch(() => {
        setCancelFailedImportIds((currentIds) => appendUniqueIds(currentIds, [fecImportId]))
      })
    },
    [onCancelFile],
  )

  return (
    <Styled.AnalysingContainer>
      <Flex flexDirection="column" gap={Spacing.S8}>
        <Text colorVariant="primary" variant="h2">
          {t('settings.organization.accounting.fec_file_import.modal.analysing_step.title')}
        </Text>
        <Text colorVariant="secondary" variant="bodyMD">
          {t('settings.organization.accounting.fec_file_import.modal.analysing_step.description')}
        </Text>

        {fecImports.map((fecImport) => {
          const { fileName, id, periodEnd, periodStart } = fecImport
          const isAnalysisComplete = isFecImportAnalysisComplete(fecImport)
          const period = isAnalysisComplete ? formatFecImportPeriod(periodStart, periodEnd, i18n.language) : undefined
          const hasCancelError = cancelFailedImportIds.includes(id)
          const cancelButton = (
            <IconButton
              aria-label={t('settings.organization.accounting.fec_file_import.modal.analysing_step.cancel_file', {
                fileName,
              })}
              disabled={cancellingFecImportIds.includes(id) || !fecImport.canDelete}
              icon="x"
              onClick={() => handleCancelFileClick(id)}
              type="button"
            />
          )

          return (
            <Flex flexDirection="column" gap={Spacing.S4} key={id} width="100%">
              <Styled.FileItem>
                <Styled.FileIconWrapper>
                  {isAnalysisComplete ? (
                    <Styled.SuccessSquircle>
                      <Icon aria-hidden="true" color={Color.Lime800} icon="check" />
                    </Styled.SuccessSquircle>
                  ) : (
                    <Icon aria-hidden="true" icon="fileText" size="md" />
                  )}
                </Styled.FileIconWrapper>
                <Styled.FileContent>
                  <Text colorVariant="primary" title={fileName} truncate variant="bodyLG" weight="semiBold">
                    {fileName}
                  </Text>
                  {isAnalysisComplete ? (
                    period && (
                      <Text colorVariant="primary" variant="bodySM">
                        {period}
                      </Text>
                    )
                  ) : (
                    <ProgressBar
                      backgroundColor={Color.Grey100}
                      progress={ANALYSING_PROGRESS_PERCENT}
                      progressColor={Color.Lavender500}
                      size="small"
                      withAnimation
                    />
                  )}
                </Styled.FileContent>
                {!isAnalysisComplete &&
                  (fecImport.canDelete ? (
                    cancelButton
                  ) : (
                    <Tooltip
                      label={t(
                        'settings.organization.accounting.fec_file_import.modal.analysing_step.cancel_file_locked_tooltip',
                      )}
                      placement="top"
                    >
                      <Tooltip.DisabledWrapper>{cancelButton}</Tooltip.DisabledWrapper>
                    </Tooltip>
                  ))}
              </Styled.FileItem>
              {hasCancelError && (
                <Text colorVariant="danger" role="alert" variant="bodySM">
                  {t('settings.organization.accounting.fec_file_import.modal.analysing_step.cancel_file_error')}
                </Text>
              )}
            </Flex>
          )
        })}

        <Flex justifyContent="flex-end">
          <Button onClick={handleCloseClick} onPointerDown={handleClosePointerDown} variant="filled">
            {t('settings.organization.accounting.fec_file_import.modal.analysing_step.close_button')}
          </Button>
        </Flex>
      </Flex>
    </Styled.AnalysingContainer>
  )
}
